import { Pause, Play, RotateCcw, Square } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { microphoneLevel, useRecordingStore } from '@/state/recording-store';
import { Button, IconButton, Spinner } from '../primitives';
import { LevelMeter, useRecordingProgress } from './media';
import { RecordingStatus } from './RecordingStatus';
import { Teleprompter } from './Teleprompter';

/**
 * The floating window's contents: one compact row with the time, the
 * microphone and the two actions that matter mid-recording.
 */
export function FloatingControls() {
  const { t } = useTranslation();
  const phase = useRecordingStore((state) => state.phase);
  const { togglePause, retake, keepTake, stop } = useRecordingStore.getState();
  const live = phase.status === 'recording' || phase.status === 'paused';
  const { elapsed } = useRecordingProgress(live);

  const notes = useRecordingStore((state) => state.notes.trim().length > 0);

  const controls = (
    <div className="flex h-[76px] shrink-0 items-center gap-3 bg-panel px-4 font-sans text-fg select-none">
      {phase.status === 'countdown' || phase.status === 'retaking' ? (
        <p className="flex flex-1 items-baseline gap-3" aria-live="assertive">
          <span className="text-[13px] text-fg-muted">{phase.status === 'retaking' ? t('record.retakeCountdown', { take: phase.take }) : t('record.countdown')}</span>
          <span key={phase.remaining} className="animate-[countdown_900ms_cubic-bezier(0.22,1,0.36,1)] text-[28px] leading-none font-semibold tabular">
            {phase.remaining}
          </span>
          {phase.status === 'retaking' && (
            <Button size="sm" variant="ghost" className="ml-auto" onClick={keepTake}>
              {t('record.keepTake')}
            </Button>
          )}
        </p>
      ) : live ? (
        <>
          <RecordingStatus phase={phase} elapsed={elapsed} compact />
          <LevelMeter level={microphoneLevel} label={t('record.micLevel')} className="text-accent-text" />
          <span className="flex flex-1 items-center justify-end gap-1.5">
            <IconButton label={`${t('record.retake')} (R)`} disabled={phase.status !== 'recording'} onClick={() => void retake()}>
              <RotateCcw size={16} />
            </IconButton>
            <IconButton label={phase.status === 'paused' ? t('record.resume') : t('record.pause')} onClick={togglePause}>
              {phase.status === 'paused' ? <Play size={17} /> : <Pause size={17} />}
            </IconButton>
            <Button variant="primary" size="sm" icon={<Square size={11} className="fill-current" />} onClick={() => void stop()}>
              {t('record.finish')}
            </Button>
          </span>
        </>
      ) : (
        <Spinner className="mx-auto text-accent-text" />
      )}
    </div>
  );

  if (!notes) return <div className="h-full bg-panel">{controls}</div>;
  return (
    <div className="flex h-full flex-col bg-panel">
      <Teleprompter className="min-h-0 flex-1" />
      <div className="border-t border-line">{controls}</div>
    </div>
  );
}